import { DripsyProvider, makeTheme } from "dripsy";
import * as React from "react";
import { Platform } from "react-native";

export const roundness = 8;

const webFont = (_: string) =>
  Platform.select({
    web: `"DM Sans", -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
    android: "DM Sans",
    default: "DM Sans",
  });

const fontForWeight = (weight: "Regular" | "Medium" | "Bold") =>
  Platform.select({
    web: `"DM Sans", -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
    android: "DM Sans",
    default: `DMSans-${weight}`,
  });

export const fontName = "DM Sans";
const theme = makeTheme({
  customFonts: {
    [fontName]: {
      "300": fontForWeight("Regular"),
      "400": fontForWeight("Regular"),
      "500": fontForWeight("Medium"),
      "600": fontForWeight("Bold"),
      "700": fontForWeight("Bold"),
      "800": fontForWeight("Bold"),
      "900": fontForWeight("Bold"),
      bold: fontForWeight("Bold"),
      default: fontForWeight("Regular"),
      normal: fontForWeight("Regular"),
    },
  },
  fonts: {
    root: fontName,
  },
  fontWeights: {
    "100": "400",
    "200": "400",
    "300": "400",
    "400": "400",
    "500": "500",
    "600": "600",
    "700": "700",
    "800": "800",
    "900": "900",
  },
  // https://www.dripsy.xyz/usage/theming/create
  text: {
    body: {
      color: "$text",
      fontFamily: webFont(fontName),
    },
    p: {
      fontSize: 16,
      color: "$text",
      fontFamily: webFont(fontName),
    },
    h1: {
      color: "$text",
      fontFamily: webFont(fontName),
      fontWeight: "700",
    },
    h2: {
      color: "$text",
      fontFamily: webFont(fontName),
      fontWeight: "700",
    },
    h3: {
      color: "$text",
      fontFamily: webFont(fontName),
      fontWeight: "700",
    },
    h4: {
      color: "$text",
      fontFamily: webFont(fontName),
      fontWeight: "700",
    },
  },
  sizes: {
    container: 1100,
  },
  layout: {
    container: {
      px: "$2",
      alignSelf: "center",
    },
    wide: {
      maxWidth: 1200,
    },
    wider: {
      maxWidth: 1400,
    },
    large: {
      maxWidth: 2100,
    },
    small: {
      maxWidth: 600,
    },
  },
  colors: {
    $greyPressedLightRgba: "rgba(0,0,0,0.05)",

    $text: "#231c00",
    $textSecondary: "#231c00",
    $background: "#ffffff",
    $backgroundPressed: "#fdfdfd",
    $secondaryBackground: "#FEFDF7",
    $secondaryBackgroundPressed: "#fcf9e8",
    $secondaryBackgroundDarker: "#F7F4E3",
    $onSecondary: "#56563e",
    $onSecondaryLight: "#747456",
    $onSecondaryLighter: "#a9a984",
    $darkGreen: "#185928",
    $darkGreenPressed: "#103c1b",
    $primary: "#237A39",
    $primaryPressed: "#1d632e",
    $lightGreen: "#239A46",
    $lightGreenPressed: "#1c7d39",
    $grey: "#d9d2bd",
    $lightGrey: "#ebe7db",
    $lightGreyPressed: "#cac2a8",
    $error: "#D32F2F",
    $errorOutlinePressedColor: "#9d1111",
    $textGrey: "#7c6947",
    $lightGreenOpacity12: "#239A461F",
    $lightGreenOpacity: "#B8C9BA",
    $errorOpacity12: "#D32F2F1F",
    $orange: "#f19623",
    $orangeLight: "#ffe5c6",
    $orangePressed: "#dd800e",
  },
  space: {
    $0: 0,
    $1: 6,
    $2: 12,
    $3: 16,
    $4: 24,
    $5: 30,
    $6: 36,
    $8: 48,
    $10: 60,
    $15: 90,
  },
  types: {
    onlyAllowThemeValues: {
      // space: "always",
    },
    reactNativeTypesOnly: true,
  },
});

type DgvTheme = typeof theme;
declare module "dripsy" {
  // eslint-disable-TODO-routes-line @typescript-eslint/no-empty-interface
  interface DripsyCustomTheme extends DgvTheme {}
}

export function Dripsy({ children }: { children: React.ReactNode }) {
  return <DripsyProvider theme={theme}>{children}</DripsyProvider>;
}
